'use client';
import React from 'react';
import { Control, useController } from 'react-hook-form';
import { ScrollArea } from '@/components/ui/scroll-area';
import { api } from '@/trpc/react';
import { AVAILABLE_COLORS, formatText } from '@acme/lib';
import ColorSelectorV2, { RANDOM_VALUE } from '@/components/ColorSelector/ColorSelectorV2';
import { WildcardCard } from '@/components/Cards/WildcardCard';

interface ClothingColorSelectorStepProps {
  control: Control<any>;
  setValue: any;
  category: string;
  wildcardModifierKey: string;
  previousValue?: string;
  themeId: string;
  gender?: 'male' | 'female';
}

export function ClothingColorSelectorStep({
  control,
  setValue,
  previousValue,
  themeId,
  gender,
  wildcardModifierKey,
  category
}: ClothingColorSelectorStepProps) {
  const { field } = useController({
    name: 'selectedModifiers',
    control
  });

  // Fetch theme data to get images for the selected clothing item
  const themeWithWildcardsQuery = api.theme.getThemeWithWildcards.useQuery(
    { themeId, gender: gender ? [gender] : undefined },
    { enabled: !!themeId }
  );

  // Get the current color value from the nested structure
  const currentColorValue = React.useMemo(() => {
    const categoryData = field.value?.[category]?.[wildcardModifierKey];
    return categoryData?.color || RANDOM_VALUE;
  }, [field.value, category, wildcardModifierKey]);

  // Find the selected clothing option to get its images
  const selectedClothingOption = React.useMemo(() => {
    if (!themeWithWildcardsQuery.data) return null;

    const options = themeWithWildcardsQuery.data.modifiers[category]?.[wildcardModifierKey] || [];
    return options.find((option: any) => option.value === previousValue);
  }, [themeWithWildcardsQuery.data, category, wildcardModifierKey, previousValue]);

  // Handle color selection
  const handleColorSelection = (colorValue: string) => {
    const currentModifiers = { ...field.value };
    const categoryData = currentModifiers[category] || {};
    const modifierData = categoryData[wildcardModifierKey] || { value: previousValue };

    if (colorValue === RANDOM_VALUE) {
      // If Shuffle is selected, remove the color property
      delete modifierData.color;
    } else {
      // Set the color in the modifier data
      modifierData.color = colorValue;
    }

    // Update the nested structure
    currentModifiers[category] = {
      ...categoryData,
      [wildcardModifierKey]: modifierData
    };

    // Update the form state
    setValue('selectedModifiers', currentModifiers);
  };

  // Get display name for the clothing item
  const displayName = React.useMemo(() => {
    if (!previousValue) return 'item';
    return formatText(previousValue.replace(/\{.*?\}/g, '').trim(), 'title');
  }, [previousValue]);

  return (
    <div className="flex flex-col h-full">
      {/* Selected Item Preview */}
      <div className="px-4 mb-6">
        <WildcardCard
          title={`${displayName}`}
          category={category}
          imageUrls={selectedClothingOption?.sampleImageUrls || []}
          layout="horizontal"
          className="w-full"
          skeleton={!selectedClothingOption}
        />
      </div>

      <ScrollArea className="flex-grow px-4">
        <div className="pb-8">
          <ColorSelectorV2
            variant="list"
            colors={AVAILABLE_COLORS.map(color => ({
              value: color.hex,
              name: color.name
            }))}
            value={
              currentColorValue === RANDOM_VALUE
                ? RANDOM_VALUE
                : AVAILABLE_COLORS.find(c => c.name === currentColorValue)?.hex
            }
            onChange={selectedColor => {
              // Convert hex back to color name if it's not the shuffle value
              if (selectedColor === RANDOM_VALUE) {
                handleColorSelection(RANDOM_VALUE);
              } else {
                const colorObj = AVAILABLE_COLORS.find(c => c.hex === selectedColor);
                if (colorObj) {
                  handleColorSelection(colorObj.name);
                }
              }
            }}
            showShuffleOption={true}
          />
        </div>
      </ScrollArea>
    </div>
  );
}
